import { VariantData, ChatMessage } from '../types';

// Generate a unique ID for messages
export function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Build context from variant data
function buildVariantContext(variantData: VariantData): string {
  const popFreq = variantData.populationFrequencies;
  const gnomadPops = popFreq?.gnomadPopulations;

  return `
VARIANT INFORMATION:
===================
Gene: ${variantData.gene}
Protein Change: ${variantData.proteinChange}
Variant Type: ${variantData.variantType}
Genomic Coordinates: ${variantData.genomicCoordinates}
HGVS Notation: ${variantData.hgvsGenomic}
Allele Change: ${variantData.alleleString}
Amino Acids: ${variantData.aminoAcids || 'N/A'}
Codons: ${variantData.codons || 'N/A'}

PATHOGENICITY PREDICTIONS:
==========================
AlphaMissense Score: ${variantData.alphaMissenseScore !== -1 ? variantData.alphaMissenseScore.toFixed(4) : 'N/A'}
AlphaMissense Classification: ${variantData.alphaMissenseClass}
SIFT Score: ${variantData.siftScore !== null ? variantData.siftScore.toFixed(3) : 'N/A'}
SIFT Prediction: ${variantData.siftPrediction || 'N/A'}
PolyPhen Score: ${variantData.polyphenScore !== null ? variantData.polyphenScore.toFixed(3) : 'N/A'}
PolyPhen Prediction: ${variantData.polyphenPrediction || 'N/A'}

CLINICAL DATA:
==============
Clinical Significance: ${variantData.clinicalSignificance || 'N/A'}
ClinVar Classifications: ${variantData.clinvarData?.clinicalSignificance?.join(', ') || 'N/A'}
Associated Conditions: ${variantData.clinvarData?.conditions?.join(', ') || 'N/A'}
Gene-Disease Correlation: ${variantData.geneDiseaseCorrelation?.summary || 'N/A'}
Typical Inheritance: ${variantData.geneDiseaseCorrelation?.inheritance?.join(', ') || 'N/A'}
Gene-Disease Evidence Level: ${variantData.geneDiseaseCorrelation?.evidenceLevel || 'N/A'}
Phenotype Correlations: ${variantData.geneDiseaseCorrelation?.phenotypeCorrelations?.map(c => `${c.phenotype} (${c.inheritance.join('/')}, ${c.evidenceLevel})`).join('; ') || 'N/A'}
Somatic Classification: ${variantData.clinvarData?.somaticClassification || 'N/A'}

POPULATION FREQUENCIES:
=======================
Global Allele Frequency: ${popFreq?.globalAF != null ? (popFreq.globalAF * 100).toFixed(4) + '%' : 'N/A'}
gnomAD Genomes: ${popFreq?.gnomadGenomes != null ? (popFreq.gnomadGenomes * 100).toFixed(4) + '%' : 'N/A'}
gnomAD Exomes: ${popFreq?.gnomadExomes != null ? (popFreq.gnomadExomes * 100).toFixed(4) + '%' : 'N/A'}
African: ${gnomadPops?.afr != null ? (gnomadPops.afr * 100).toFixed(4) + '%' : 'N/A'}
American: ${gnomadPops?.amr != null ? (gnomadPops.amr * 100).toFixed(4) + '%' : 'N/A'}
Ashkenazi Jewish: ${gnomadPops?.asj != null ? (gnomadPops.asj * 100).toFixed(4) + '%' : 'N/A'}
East Asian: ${gnomadPops?.eas != null ? (gnomadPops.eas * 100).toFixed(4) + '%' : 'N/A'}
Finnish: ${gnomadPops?.fin != null ? (gnomadPops.fin * 100).toFixed(4) + '%' : 'N/A'}
Non-Finnish European: ${gnomadPops?.nfe != null ? (gnomadPops.nfe * 100).toFixed(4) + '%' : 'N/A'}
South Asian: ${gnomadPops?.sas != null ? (gnomadPops.sas * 100).toFixed(4) + '%' : 'N/A'}

EXTERNAL DATABASES:
===================
COSMIC IDs: ${variantData.cosmicIds?.length > 0 ? variantData.cosmicIds.slice(0, 5).join(', ') : 'N/A'}
HGMD IDs: ${variantData.hgmdIds?.length > 0 ? variantData.hgmdIds.join(', ') : 'N/A'}
PubMed References: ${variantData.pubmedIds?.length > 0 ? variantData.pubmedIds.length + ' publications' : 'N/A'}

TRANSCRIPT INFORMATION:
=======================
Canonical Transcript: ${variantData.canonicalTranscript || 'N/A'}
Protein ID: ${variantData.proteinId || 'N/A'}
Total Protein-Coding Transcripts Affected: ${variantData.transcriptCount}
Molecular Mechanism: ${variantData.molecularMechanism}
Structural Impact: ${variantData.structuralImpact}

ALPHAGENOME PREDICTION:
=======================
${variantData.alphaGenomePrediction}

CLINICAL INTERPRETATION:
========================
${variantData.description}
`;
}

// System prompt for the chat
function getSystemPrompt(variantContext: string): string {
  return `You are an expert clinical genomics and cardiology assistant. You have access to comprehensive data about a specific variant that the user is analyzing. The user is a cardiologist or clinical geneticist.

${variantContext}

SCOPE — WHAT YOU CAN DISCUSS:
You are allowed (and encouraged) to discuss ANY medical topic related to the variant, gene, or associated cardiac/genetic condition, including but not limited to:
- The variant data above: scores, classifications, population frequencies, predictions
- The associated cardiac condition(s): disease features, natural history, prognosis, epidemiology, pathophysiology
- Clinical management: surveillance protocols, imaging recommendations, exercise guidance, device therapy indications
- Pharmacotherapy: medications, contraindicated drugs, dosing considerations
- Family screening and cascade genetic testing strategies
- Genetic counseling considerations, penetrance, expressivity
- Guideline recommendations (ESC, AHA/ACC, HRS, ClinGen)
- Pre-authorization letters: help draft medical necessity letters for insurance (genetic testing, devices, imaging, procedures)
- Differential diagnosis and phenotype-genotype correlation
- Risk stratification (e.g., SCD risk in HCM, arrhythmia risk)
- Patient education materials and communication strategies
- Research context: ongoing clinical trials, emerging therapies

STRICT BOUNDARY — WHAT YOU MUST REFUSE:
You must politely decline ANY topic that is not medical or clinical in nature. This includes (but is not limited to): coding/programming help, general knowledge questions, creative writing, travel, recipes, financial advice, legal advice (beyond medical necessity documentation), entertainment, politics, or any other non-medical subject. If asked, respond with: "I'm specialized in clinical genomics and cardiology. I can only help with medical questions related to this variant and its associated conditions. How can I help you clinically?"

STYLE:
- Write for an expert clinician — use appropriate medical terminology
- Be thorough but concise
- Reference specific data from the variant analysis when relevant
- For clinical recommendations, cite guideline sources when possible
- When drafting letters or documents, use professional medical language
- Do NOT use markdown formatting (no asterisks, hash signs, or code blocks); use plain text only

IMPORTANT: When discussing this specific patient's variant data, base your answers on the actual data provided above. For general medical knowledge about the condition, you may draw on your training data. Always note that clinical decisions should integrate the full clinical picture.`;
}

// Main chat function
export async function chatAboutVariant(
  userMessage: string,
  variantData: VariantData,
  conversationHistory: ChatMessage[]
): Promise<string> {
  const response = await fetch('/api/chat_variant', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      userMessage,
      variantData,
      conversationHistory,
    }),
  });

  const rawText = await response.text();
  let data: unknown = null;

  try {
    data = rawText ? JSON.parse(rawText) : null;
  } catch {
    data = null;
  }

  if (!response.ok) {
    const message =
      (data && typeof data === 'object' && 'detail' in data && typeof (data as any).detail === 'string' && (data as any).detail)
      || rawText
      || 'Failed to get response. Please try again.';
    throw new Error(message);
  }

  if (data && typeof data === 'object' && 'response' in data && typeof (data as any).response === 'string') {
    return (data as any).response;
  }

  throw new Error('Failed to get response. Please try again.');
}

// Suggested questions based on variant data
export function getSuggestedQuestions(variantData: VariantData): string[] {
  const questions: string[] = [];

  // Pathogenicity question
  questions.push(`Is ${variantData.gene} ${variantData.proteinChange} considered pathogenic?`);

  // Disease features question
  const conditions = variantData.clinvarData?.conditions;
  if (conditions && conditions.length > 0) {
    questions.push(`What are the key clinical features of ${conditions[0]}?`);
  } else {
    questions.push(`What cardiac conditions are associated with ${variantData.gene} variants?`);
  }

  // Management question
  questions.push(`What surveillance protocol is recommended for carriers of this variant?`);

  // Risk stratification
  questions.push(`How should SCD risk be assessed in this context?`);

  // Pre-auth letter
  questions.push(`Draft a pre-authorization letter for genetic testing of family members`);

  // Population frequency question
  if (variantData.populationFrequencies?.globalAF != null) {
    questions.push(`How rare is this variant in the population?`);
  }

  // AlphaMissense question
  if (variantData.alphaMissenseScore !== -1) {
    questions.push(`What does the AlphaMissense score of ${variantData.alphaMissenseScore.toFixed(2)} mean?`);
  }

  // Return first 5 unique questions
  return questions.slice(0, 5);
}